import { useCallback, useEffect, useState } from 'react';
import { usePaging } from 'hooks/usePaging';
import { adminGetBackgroundJobs, BackgroundJobItem } from 'services/backgroundJobs';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import dayjs from 'dayjs';
import { useRoomContext } from 'contexts/RoomContext';
import { downloadFile } from 'utils/downloadFile';

const SIZE_PAGE = 15;

export const useBackgroundJobsInjection = () => {
  const [jobs, setJobs] = useState<BackgroundJobItem[]>([]);
  const [userSelect, setUserSelect] = useState<string>();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>();

  // Track download progress per job ID
  const [downloadingJobs, setDownloadingJobs] = useState<Record<string, number>>({});

  const { pagingInfo, setCurrentPage, setTotal } = usePaging({
    rowsPerPage: SIZE_PAGE
  });
  const { users } = useRoomContext();

  const handleGetData = useCallback(
    async (
      query: Partial<{
        userId: string;
        type: string;
        dateFrom: string;
        dateTo: string;
        page: number;
        limit: number;
      }>
    ) => {
      const resp = await adminGetBackgroundJobs(query);
      setJobs(resp.data);
      setTotal(resp.total);
    },
    [setTotal]
  );

  const { asyncCallback, isLoading: loading } = useAsyncCallback(handleGetData, []);

  useEffect(() => {
    asyncCallback({
      dateFrom:
        fromDate && dayjs(fromDate).isValid()
          ? dayjs(fromDate).startOf('days').toISOString()
          : undefined,
      dateTo:
        toDate && dayjs(toDate).isValid() ? dayjs(toDate).endOf('days').toISOString() : undefined,
      page: pagingInfo.currentPage,
      limit: SIZE_PAGE,
      userId: userSelect || undefined,
      type: typeFilter || undefined
    });
  }, [typeFilter, pagingInfo.currentPage, userSelect, fromDate, toDate, asyncCallback]);

  const handleChangePage = useCallback(
    (page: number) => {
      setCurrentPage(page);
    },
    [setCurrentPage]
  );

  const handleDownload = async (jobId: string) => {
    const url = `/background-jobs/${jobId}/download`;

    // Start progress at 0
    setDownloadingJobs((prev) => ({ ...prev, [jobId]: 0 }));

    await downloadFile(url, undefined, (progress) => {
      setDownloadingJobs((prev) => ({ ...prev, [jobId]: progress }));
    });

    // Clear progress when done
    setDownloadingJobs((prev) => {
      const newState = { ...prev };
      delete newState[jobId];
      return newState;
    });
  };

  return {
    typeFilter,
    setTypeFilter,
    userSelect,
    setUserSelect,
    users,
    jobs,
    pagingInfo,
    handleChangePage,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    loading,
    handleDownload,
    downloadingJobs
  };
};
